import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';

import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';

import { Observable, finalize } from 'rxjs';

import { Benutzer } from '../../models/benutzer.model';
import { Rolle } from '../../models/rolle.model';
import { Filiale } from '../../models/filiale.model';

type DialogMode = 'create' | 'edit';

export interface BenutzerFormDialogResult {
  email: string;
  benutzername: string | null;
  password: string;
  vorname: string;
  name: string;
  rollen: string[];
  filialen: number[];
  tenantId?: string | null;
}

interface BenutzerFormDialogData {
  mode: DialogMode;
  benutzer?: Benutzer;
  rollen: Rolle[];
  filialen: Filiale[];
  submitRequest: (result: BenutzerFormDialogResult) => Observable<unknown>;
  isCopagoAdminMode?: boolean;
  isCurrentUser?: boolean;
}

interface RolleOption {
  id: string;
  label: string;
  badge?: string;
}

interface FilialeOption {
  id: number;
  label: string;
  icon: string;
}

@Component({
  selector: 'app-benutzer-form-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatIconModule,
    MatSnackBarModule,
  ],
  templateUrl: './benutzer-form-dialog.component.html',
  styleUrl: './benutzer-form-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BenutzerFormDialogComponent {
  private readonly fb = new FormBuilder();

  protected readonly selectedRollen = signal<string[]>([]);
  protected readonly selectedFilialen = signal<number[]>([]);
  protected readonly roleError = signal(false);
  protected readonly showPassword = signal(false);
  protected readonly isSaving = signal(false);

  protected get isCurrentUserEdit(): boolean {
    return this.data.mode === 'edit' && this.data.isCurrentUser === true;
  }

  protected rollen: RolleOption[] = [];
  protected filialen: FilialeOption[] = [];

  protected readonly form = this.fb.nonNullable.group({
    tenantId: [''],
    email: [
      '',
      [
        Validators.required,
        Validators.pattern(/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/),
      ],
    ],
    benutzername: [''],
    passwort: [''],
    vorname: ['', [Validators.required]],
    nachname: ['', [Validators.required]],
  });

  constructor(
    private readonly dialogRef: MatDialogRef<BenutzerFormDialogComponent, boolean>,
    private readonly snackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) protected readonly data: BenutzerFormDialogData
  ) {
    this.rollen = this.data.rollen.map((rolle) => ({
      id: rolle.id,
      label: rolle.name,
      badge: rolle.istAdminRolle ? 'Administrator' : undefined,
    }));

    this.filialen = this.data.filialen.map((filiale) => ({
      id: filiale.id,
      label: `${filiale.filialNummer} - ${filiale.name}`,
      icon: 'apartment',
    }));

    if (this.data.mode === 'edit' && this.data.benutzer) {
      this.form.patchValue({
        email: this.data.benutzer.email ?? '',
        benutzername: this.data.benutzer.benutzername ?? '',
        passwort: '',
        vorname: this.data.benutzer.vorname ?? '',
        nachname: this.data.benutzer.nachname ?? '',
        tenantId: this.data.benutzer.id ?? '',
      });

      this.selectedRollen.set(
        (this.data.benutzer.rollen ?? []).map((rolle) => rolle.id)
      );

      this.selectedFilialen.set(this.data.benutzer.allowedFilialen ?? []);
    }
  }

  protected get title(): string {
    return this.data.mode === 'edit' ? 'Benutzer bearbeiten' : 'Benutzer anlegen';
  }

  protected get description(): string {
    if (this.data.isCopagoAdminMode) {
      return this.data.mode === 'edit'
        ? 'Aktualisiere die SuperAdmin-Daten dieses Tenants.'
        : 'Lege einen neuen Tenant mit zugehörigem SuperAdmin an.';
    }

    if (this.isCurrentUserEdit) {
      return 'Aktualisiere deine Benutzerdaten. Rollen und Filialen können nicht selbst geändert werden.';
    }

    return this.data.mode === 'edit'
      ? 'Aktualisiere Benutzer, Rollen und Filialzuordnung.'
      : 'Lege einen neuen Benutzer an und weise Rollen und Filialen zu.';
  }

  protected get submitLabel(): string {
    return this.data.mode === 'edit' ? 'Speichern' : 'Benutzer anlegen';
  }

  protected close(): void {
    if (this.isSaving()) {
      return;
    }

    this.dialogRef.close(false);
  }

  protected togglePasswordVisibility(): void {
    this.showPassword.update((value) => !value);
  }

  protected toggleRolle(id: string): void {
    this.roleError.set(false);

    this.selectedRollen.update((items) =>
      items.includes(id) ? items.filter((x) => x !== id) : [...items, id]
    );
  }

  protected toggleFiliale(id: number): void {
    this.selectedFilialen.update((items) =>
      items.includes(id) ? items.filter((x) => x !== id) : [...items, id]
    );
  }

  protected isRolleSelected(id: string): boolean {
    return this.selectedRollen().includes(id);
  }

  protected isFilialeSelected(id: number): boolean {
    return this.selectedFilialen().includes(id);
  }

  protected submit(): void {
    if (this.isSaving()) {
      return;
    }

    this.roleError.set(false);

    if (this.data.mode === 'create') {
      const passwort = this.form.controls.passwort.value;

      if (!passwort || passwort.length < 8) {
        this.form.controls.passwort.setErrors({ minlength: true });
      }
    }

    if (this.data.isCopagoAdminMode && !this.form.controls.tenantId.value.trim()) {
      this.form.controls.tenantId.setErrors({ required: true });
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (
      !this.data.isCopagoAdminMode &&
      !this.isCurrentUserEdit &&
      !this.selectedRollen().length
    ) {
      this.roleError.set(true);
      return;
    }

    const rawValue = this.form.getRawValue();

    const result: BenutzerFormDialogResult = {
      tenantId: rawValue.tenantId || null,
      email: rawValue.email,
      benutzername: rawValue.benutzername || null,
      password: rawValue.passwort,
      vorname: rawValue.vorname,
      name: rawValue.nachname,
      rollen: this.selectedRollen(),
      filialen: this.selectedFilialen(),
    };

    this.isSaving.set(true);

    this.data.submitRequest(result)
      .pipe(finalize(() => this.isSaving.set(false)))
      .subscribe({
        next: () => {
          this.dialogRef.close(true);
        },
        error: () => {
          this.snackBar.open(
            'Speichern fehlgeschlagen. Bitte erneut versuchen.',
            '×',
            {
              duration: 5000,
              horizontalPosition: 'right',
              verticalPosition: 'top',
              panelClass: ['error-snackbar'],
            }
          );
        },
      });
  }
}